/**
 * client/src/pages/security-centre/master/api.ts
 *
 * Device-limit + master-passkey spec PR B (#1605, module 02 §3). Thin
 * begin/complete wrappers around the server's
 * /api/auth/webauthn/master/{designate,arming-cancel,transfer/instant,
 * transfer/hold,veto}/{begin,complete} routes + the GET /master/status read,
 * plus the master-pinned credential-revoke begin route
 * (POST /api/auth/webauthn/credentials/:id/revoke/begin). No client signing
 * keys anywhere — every ceremony only relays a WebAuthn assertion the server
 * verifies (Rule 11), same posture as every other ceremony transport in this
 * codebase (bank-account-transport.ts, use-stamp-ceremony.ts).
 *
 * IMPORTANT id shapes (verified against server/logic/webauthn/master-designation/*):
 *   - `candidateDbId` / `toCredentialId` are the `webauthn_credentials.id`
 *     DB uuid (NOT the WebAuthn `credentialId` base64url string) — the server
 *     resolves designation/transfer targets by that column.
 *   - The revoke and rename routes address a credential by its WebAuthn
 *     `credentialId` string (`:id` route param), matching the existing
 *     DELETE /api/auth/webauthn/credentials/:id call already in index.tsx.
 */

import type {
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import { apiRequest, postJSON } from "@/lib/queryClient";
import type { MasterStatus } from "../types";

const BASE = "/api/auth/webauthn/master";

interface BeginResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
}

// ---------------------------------------------------------------------------
// Status (safe GET — also runs the lazy hold-completion sweep server-side)
// ---------------------------------------------------------------------------

export function fetchMasterStatus(): Promise<MasterStatus> {
  return apiRequest<MasterStatus>(`${BASE}/status`);
}

// ---------------------------------------------------------------------------
// First designation (AC-7, AC-20, AC-25) — fresh UV FROM the candidate.
// ---------------------------------------------------------------------------

export function beginDesignate(candidateDbId: string): Promise<BeginResponse> {
  return postJSON<BeginResponse>(`${BASE}/designate/begin`, { candidateCredentialId: candidateDbId });
}

export function completeDesignate(
  response: AuthenticationResponseJSON,
): Promise<{ success: true; holdId: string; executesAt: string }> {
  return postJSON(`${BASE}/designate/complete`, { response });
}

// ---------------------------------------------------------------------------
// Arming cancel (AC-7) — any active credential, cancels the 24h window.
// ---------------------------------------------------------------------------

export function beginArmingCancel(): Promise<BeginResponse> {
  return postJSON<BeginResponse>(`${BASE}/arming-cancel/begin`, {});
}

export function completeArmingCancel(
  response: AuthenticationResponseJSON,
): Promise<{ success: true; cancelled: boolean }> {
  return postJSON(`${BASE}/arming-cancel/complete`, { response });
}

// ---------------------------------------------------------------------------
// Instant transfer / clear (AC-9) — CURRENT master fresh UV only.
// Omit `toCredentialDbId` to clear the master instead of transferring it.
// ---------------------------------------------------------------------------

export function beginInstantTransfer(): Promise<BeginResponse> {
  return postJSON<BeginResponse>(`${BASE}/transfer/instant/begin`, {});
}

export function completeInstantTransfer(
  response: AuthenticationResponseJSON,
  toCredentialDbId?: string,
): Promise<{ success: true; transferred: boolean; cleared: boolean }> {
  return postJSON(`${BASE}/transfer/instant/complete`, { response, toCredentialId: toCredentialDbId });
}

// ---------------------------------------------------------------------------
// 72h transfer hold (AC-10) — any active credential initiates.
// ---------------------------------------------------------------------------

export function beginTransferHold(): Promise<BeginResponse> {
  return postJSON<BeginResponse>(`${BASE}/transfer/hold/begin`, {});
}

export function completeTransferHold(
  response: AuthenticationResponseJSON,
  toCredentialDbId: string,
): Promise<{ success: true; holdId: string; executesAt: string }> {
  return postJSON(`${BASE}/transfer/hold/complete`, { response, toCredentialId: toCredentialDbId });
}

// ---------------------------------------------------------------------------
// Veto (AC-10) — CURRENT master fresh UV only.
// ---------------------------------------------------------------------------

export function beginVeto(): Promise<BeginResponse> {
  return postJSON<BeginResponse>(`${BASE}/veto/begin`, {});
}

export function completeVeto(
  response: AuthenticationResponseJSON,
): Promise<{ success: true; vetoed: boolean }> {
  return postJSON(`${BASE}/veto/complete`, { response });
}

// ---------------------------------------------------------------------------
// Master-pinned credential revoke (AC-27) — begin reports whether a ceremony
// is needed at all (no master → session-auth revoke, unchanged behaviour).
// ---------------------------------------------------------------------------

/**
 * THE 202 BODY, and why it needs a type of its own.
 *
 * `POST .../revoke/begin` answers **202** — a 2xx — when the ladder opens a
 * 48-hour hold instead of removing now (server/routes/webauthn-credential-
 * revoke-hold.ts, spec module 04 AC-23/AC-25). Nothing was refused, so it is
 * deliberately not a 4xx, and `apiRequest` therefore RESOLVES it like any other
 * success. A caller that only tests `ceremonyRequired` reads `undefined`,
 * concludes "no ceremony needed", fires the DELETE — which 202s as held too —
 * and reports a removal that did not happen while a hold nobody was shown is
 * pending. That was the reported defect; this variant is what makes the branch
 * unmissable at the type level.
 */
export interface RemovalHeldResult {
  held: true;
  code: "REMOVAL_HELD";
  holdId: string;
  /** ISO-8601 UTC. Can only ever move LATER (a Forensic Pause is refunded at
   *  lift), so it is safe to display but must not be cached as fixed. */
  expiresAt: string;
  pauseActive: boolean;
  guardiansAddressed: number;
  sessionsAddressed: number;
  /** The server's own copy, already honesty-reviewed against what the
   *  notification rail can actually do — rendered verbatim, never re-worded
   *  client-side (§Friction Rule clause 2). */
  message: string;
}

export type RevokeBeginResult =
  | { ceremonyRequired: false }
  | { ceremonyRequired: true; options: PublicKeyCredentialRequestOptionsJSON }
  | RemovalHeldResult;

/** Both revoke legs can answer with a hold, so the discriminator lives here
 *  rather than at either call site. Keyed on the server's `code`, not on the
 *  HTTP status, because `apiRequest` does not surface the status of a 2xx. */
export function isRemovalHeld(result: unknown): result is RemovalHeldResult {
  return (result as RemovalHeldResult | null)?.code === "REMOVAL_HELD";
}

export function beginCredentialRevoke(credentialId: string): Promise<RevokeBeginResult> {
  return postJSON<RevokeBeginResult>(
    `/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}/revoke/begin`,
    {},
  );
}

/** DELETE the credential. `assertionResponse` is required only when a master
 *  exists (server 400s a missing one in that case — beginCredentialRevoke
 *  told the caller which branch applies via `ceremonyRequired`).
 *
 *  May ALSO answer 202 held: the executing route reads the hold standing and,
 *  when a hold is pending rather than ripe, writes the same body. The caller
 *  must therefore check `isRemovalHeld` on this result too — a hold reached
 *  here means the removal has NOT happened. */
export function completeCredentialRevoke(
  credentialId: string,
  assertionResponse?: AuthenticationResponseJSON,
): Promise<{ success: true } | RemovalHeldResult> {
  return apiRequest(`/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ assertionResponse }),
  });
}

// ---------------------------------------------------------------------------
// Rename (module 01 §4, AC-22) — metadata only, no fresh UV.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Clear a post-lift incident hold (passkey boundary gaps module 02, AC-8) —
// fresh UV from a DIFFERENT, unheld passkey (the master when one exists).
// ---------------------------------------------------------------------------

export function beginClearIncidentHold(credentialId: string): Promise<BeginResponse> {
  return postJSON<BeginResponse>(
    `/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}/clear-incident-hold/begin`, {},
  );
}

export function completeClearIncidentHold(
  credentialId: string,
  assertionResponse: AuthenticationResponseJSON,
): Promise<{ cleared: true }> {
  return postJSON(
    `/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}/clear-incident-hold`,
    { assertionResponse },
  );
}

export function renameCredential(credentialId: string, deviceName: string): Promise<{ success: true }> {
  return apiRequest(`/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceName }),
  });
}
